

























































































































import { ExceptionReportApi } from '@/api/exception-reports';
import { BlankDataFieldsPostCallRequestFilters } from '@/api/requests/exception-reports/blank-data-fields-post-call-request-filters';
import { Forbidden } from '@/api/responses/forbidden';
import blankDataFieldsPostCallTable from '@/components/exception-reports/blank-data-fields-post-call-table.vue';
import loading from '@/components/loading.vue';
import { BlankDataFieldsPostCall } from '@/shared/exception-reports/blank-data-fields-post-call';
import { PartnerPractice } from '@/shared/partner-practice';
import { Role } from '@/shared/roles/role';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const usersModule = namespace('users');
const exceptionReportApi = new ExceptionReportApi();

@Component({
    components: {
        blankDataFieldsPostCallTable,
        loading,
    },
})
export default class BlankDataFieldsPostCallExceptionReport extends Vue {
    @partnerPracticesModule.Getter('partnerPracticesForCurrentUser') private partnerPractices!: Array<PartnerPractice>;

    @usersModule.Getter('roles') private roles!: Array<Role>;

    private isLoading: boolean = true;
    private partnerPracticeFilter: string | null = null;
    private accountNoFilter: number | null = null;
    private clientFirstNameFilter: string | null = null;
    private clientLastNameFilter: string | null = null;
    private lastCallDatePeriodFilter: number | null = null;
    private lastCallDateFromFilter: string | Date | null = null;
    private lastCallDateToFilter: string | Date | null = null;
    private userFilter: string | null = null;
    private userRoleFilter: string | null = null;
    private blankDataFieldTypeFilter: number | null = null;
    private clients: Array<BlankDataFieldsPostCall> = new Array<BlankDataFieldsPostCall>();
    private reportName: string = 'Blank Data Fields Post Call';
    private emptyText: string | null = `Please enter some search filters and click Filter to show ${this.reportName}`;

    private datePeriods = new Array<{ id: number, value: string }>(...[
        { id: 0, value: 'Current Day' },
        { id: 1, value: 'Current Week' },
        { id: 2, value: 'Previous Week' },
        { id: 3, value: 'Current Month' },
        { id: 4, value: 'Previous Month' },
        { id: 5, value: 'Current Quarter' },
        { id: 6, value: 'Previous Quarter' },
        { id: 7, value: 'Current Year' },
        { id: 8, value: 'Previous Year' },
        { id: 9, value: 'Custom Dates' },
    ]);

    private blankFieldTypes = new Array<{ id: number, value: string }>(...[
        { id: 0, value: 'Postcode' },
        { id: 1, value: 'Date of Birth' },
        { id: 2, value: 'Email Address' },
        { id: 3, value: 'Telephone' },
        { id: 4, value: 'Mobile' },
        { id: 5, value: 'Relationship Status' },
        { id: 6, value: 'Will Executors' },
        { id: 7, value: 'Children' },
        { id: 8, value: 'Children Under 18' },
        { id: 9, value: 'Homeowner' },
        { id: 10, value: 'Business Owner' },
    ]);

    private get isCustomDatePeriod(): boolean {
        return this.lastCallDatePeriodFilter === 9;
    }

    private get requestFilters(): BlankDataFieldsPostCallRequestFilters {
        return {
            partnerPracticeId: this.partnerPractices.length === 1 ? this.partnerPractices[0].id! : this.partnerPracticeFilter,
            accountNo: this.accountNoFilter,
            clientFirstName: this.clientFirstNameFilter,
            clientLastName: this.clientLastNameFilter,
            lastCallDatePeriod: this.lastCallDatePeriodFilter,
            lastCallDateFrom: this.lastCallDateFromFilter,
            lastCallDateTo: this.lastCallDateToFilter,
            userId: this.userFilter,
            userRoleId: this.userRoleFilter,
            blankDataFieldType: this.blankDataFieldTypeFilter,
        };
    }

    @Watch('lastCallDatePeriodFilter')
    private async onLastCallDatePeriodFilterChanged(): Promise<void> {
        this.lastCallDateFromFilter = null;
        this.lastCallDateToFilter = null;
    }

    public async getReportDataAsync(): Promise<void> {
        if (!(await this.$validator.validateAll())) {
            return;
        }

        this.isLoading = true;

        try {
            this.clients = await exceptionReportApi.getBlankDataFieldsPostCallAsync(this.requestFilters);
            this.emptyText = `No ${this.reportName} found matching the requested search filters`;
        } catch (e) {
            if (e instanceof Forbidden) {
                this.$router.push({ name: 'forbidden' });
            } else {
                this.$snackbar.open({ message: `Could not load ${this.reportName}.`, type: 'is-danger' });
            }
        }

        this.isLoading = false;
    }

    private async exportReportAsync(): Promise<void> {
        if (!(await this.$validator.validateAll())) {
            return;
        }

        this.isLoading = true;

        try {
            const response = await exceptionReportApi.getBlankDataFieldsPostCallExportAsync(this.requestFilters);

            if (response == null) {
                this.$snackbar.open({ message: `No ${this.reportName} found matching the requested search filters.`, type: 'is-danger' });
                return;
            }

            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.rel = 'noopener';
            link.href = window.URL.createObjectURL(blob);
            link.download = 'blank-data-fields-post-call.xlsx';
            link.target = '_blank';
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (e) {
            if (e instanceof Forbidden) {
                this.$router.push({ name: 'forbidden' });
            } else {
                this.$snackbar.open({ message: `Could not download ${this.reportName}, please try again.`, type: 'is-danger' });
            }
        }

        this.isLoading = false;
    }

    private clearFilters(): void {
        this.partnerPracticeFilter = null;
        this.accountNoFilter = null;
        this.clientFirstNameFilter = null;
        this.clientLastNameFilter = null;
        this.lastCallDatePeriodFilter = null;
        this.lastCallDateFromFilter = null;
        this.lastCallDateToFilter = null;
        this.userFilter = null;
        this.userRoleFilter = null;
        this.blankDataFieldTypeFilter = null;
    }

    private async mounted(): Promise<void> {
        this.isLoading = true;

        await Promise.all([
            this.$store.dispatch(`partnerPractices/loadPartnerPracticesAsync`),
            this.$store.dispatch(`types/loadPartnerPracticeTypesAsync`),
            this.$store.dispatch(`users/loadRolesAsync`),
        ]);

        this.isLoading = false;
    }
}
